/**
 * Team members component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { listTeamMembers, inviteUser, assignRole, type TeamMember, type InviteUserRequest } from '../../api/teams';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useToastStore } from '../../stores/toast';
import { Users, UserPlus, Mail, User as UserIcon, Shield, Search, X } from 'lucide-react';

export function TeamMembers() {
  const { tenantId, projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['team-members', tenantId, projectId],
    queryFn: () => listTeamMembers({
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
    }),
    enabled: !!(tenantId || projectId),
  });

  const inviteMutation = useMutation({
    mutationFn: (request: InviteUserRequest) => inviteUser(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRoleId('');
      addToast({
        message: 'Invitation sent successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to send invitation',
        type: 'error',
      });
    },
  });

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      addToast({
        message: 'Please enter an email address',
        type: 'error',
      });
      return;
    }

    inviteMutation.mutate({
      email: inviteEmail.trim(),
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
      role_id: inviteRoleId || undefined,
    });
  };

  if (!tenantId && !projectId) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Please select a tenant or project to view team members
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {data?.total || 0} member{data?.total !== 1 ? 's' : ''}
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          size="sm"
        >
          <UserPlus className="h-4 w-4" />
          Invite Member
        </Button>
      </div>

      {/* Members List */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading members...</div>
      ) : !data?.members || data.members.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Users className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p>No team members yet</p>
          <Button
            onClick={() => setShowInviteModal(true)}
            variant="outline"
            className="mt-4"
          >
            <UserPlus className="h-4 w-4" />
            Invite First Member
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {data.members.map((member) => (
            <div
              key={member.user_id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                  {member.full_name?.[0]?.toUpperCase() || member.username[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {member.full_name || member.username}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {member.email}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {member.role_name && (
                  <Badge variant="info">
                    <Shield className="h-3 w-3" />
                    {member.role_name}
                  </Badge>
                )}
                <Badge variant={member.status === 'accepted' ? 'success' : 'warning'}>
                  {member.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div 
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInviteModal(false)}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role (Optional)
                </label>
                <Input
                  type="text"
                  value={inviteRoleId}
                  onChange={(e) => setInviteRoleId(e.target.value)}
                  placeholder="Role ID (optional)"
                  leftIcon={<Shield className="h-5 w-5 text-gray-400" />}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Leave empty to assign default role
                </p>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                <Button
                  variant="ghost"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                  className="flex-1"
                >
                  {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


